import type { RelationOutputForBuyer, RelationQuery } from "@/api/custom_types";
import { ACTIVE_OR_BANNED } from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { usePersistedServerTableState } from "@/hooks/usePersistedServerTableState";
import { useTranslation } from "@/hooks/useTranslation";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useDecideRelation, useGetRelations } from "@/services";
import { currentUserAtom } from "@/store/login";
import { getTimeSinceText } from "@/utils/timestamp";
import { formatNumber } from "@/utils/tools";
import {
	type StatusTranslation,
	getRelationStateTranslation,
} from "@/utils/translations";
import {
	Chip,
	type ChipVariant,
	DataTable,
	Icon,
	Typography,
	toast,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useAtomValue } from "jotai";
import { type FC, useCallback, useMemo, useState } from "react";

import AddPaymentDialog from "@/pages/payment/shared/AddPaymentDialog";
import RelationPriceCell from "./RelationPriceCell";
import RelationsSingleAction, {
	type RelationRow,
} from "./RelationsSingleAction";
import {
	BalanceAdjustmentDialog,
	RelationsAddRequest,
	RelationsFilterForm,
	RelationsPendingRequests,
} from "./components";
import { usePendingSet } from "./hooks";
import type { FilterFormData } from "./types";

const normalizeStatuses = (statuses: RelationQuery[] | undefined) =>
	statuses?.length ? statuses : undefined;

const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

const BuyerRelations: FC = () => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const user = useAtomValue(currentUserAtom);
	const decideRelationMutation = useDecideRelation();

	const [filters, setFilters] = useState<FilterFormData>({
		buyer_name: null,
		seller_name: null,
		status: [...ACTIVE_OR_BANNED],
	});

	const server = usePersistedServerTableState("buyer-relations");

	const handleSearch = useCallback(
		(values: FilterFormData) => {
			setFilters(values);
			server.onPageChange(0);
		},
		[server],
	);

	const { isPending, setItemPending } =
		usePendingSet<RelationOutputForBuyer["id"]>();

	const [adjustmentRelation, setAdjustmentRelation] =
		useState<RelationRow | null>(null);

	const {
		data: relations,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useGetRelations({
		query: {
			page: server.pageIndex + 1,
			page_size: server.pageSize,
			seller_id: user?.id,
			statuses: normalizeStatuses(filters.status as RelationQuery[]),
			buyer_name: filters.buyer_name,
			seller_name: filters.seller_name,
		},
	});

	const handleToggleBanAction = useCallback(
		(id: RelationOutputForBuyer["id"], action: "BAN" | "UNBAN") => {
			setItemPending(id, true);
			decideRelationMutation.mutate(
				{
					path: { relation_id: id },
					body: { action: { action_type: action } },
				},
				{
					onSuccess: async () => {
						toast.success(
							action === "BAN"
								? t("relation.successBanAction")
								: t("relation.successUnbanAction"),
						);
					},
					onSettled: () => setItemPending(id, false),
				},
			);
		},
		[decideRelationMutation, setItemPending, t],
	);

	const columns = useMemo<ColumnDef<RelationRow>[]>(
		() => [
			{
				id: "buyer_display_name",
				header: t("relation.tableBuyerName"),
				enableSorting: false,
				cell: ({ row }) => (
					<RelationNameLink
						name={row.original.buyer_display_name}
						relationId={row.original.id}
						party="buyer"
					/>
				),
			},
			{
				id: "state",
				header: t("relation.tableStatus"),
				enableSorting: false,
				cell: ({ row }) => {
					const { color, text } = getRelationStateTranslation(
						row.original.state,
						t,
					);
					return (
						<Chip variant={STATUS_COLOR_TO_CHIP_VARIANT[color]} size="sm">
							{text}
						</Chip>
					);
				},
			},
			{
				id: "is_allowed_to_order",
				header: t("relation.tableAllowedToOrder"),
				enableSorting: false,
				cell: ({ row }) => {
					const isAllowed =
						row.original.schema_type === "for_seller"
							? row.original.resolved_is_allowed_to_order.value
							: row.original.is_allowed_to_order;

					return isAllowed ? (
						<Icon name="check-line" size="sm" className="text-success-text" />
					) : (
						<Icon name="close-line" size="sm" className="text-danger-text" />
					);
				},
			},
			{
				id: "current_debt",
				header: t("relation.tableDebt"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.current_debt, "price"),
			},
			{
				id: "max_debt",
				header: t("relation.tableCredit"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.max_debt, "price"),
			},
			{
				id: "final_price",
				header: t("relation.tableFinalPrice"),
				enableSorting: false,
				cell: ({ row }) =>
					row.original.schema_type === "for_seller" ? (
						<RelationPriceCell price={row.original.final_price} />
					) : (
						formatNumber(row.original.final_price, "price")
					),
			},
			{
				id: "last_paid_on",
				header: t("relation.tableLastPaidOn"),
				enableSorting: false,
				cell: ({ row }) =>
					row.original.last_paid_on
						? getTimeSinceText(row.original.last_paid_on, t, lang)
						: "-",
			},
			{
				id: "actions",
				header: "",
				enableSorting: false,
				cell: ({ row }) => (
					<RelationsSingleAction
						selectedRelation={row.original}
						banPending={isPending(row.original.id)}
						onToggleBan={handleToggleBanAction}
						onAdjust={setAdjustmentRelation}
					/>
				),
			},
		],
		[t, lang, isPending, handleToggleBanAction],
	);

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<Typography variant="title-md" as="h1">
					{t("dashboard.menuBuyerRelations")}
				</Typography>

				<div className="flex flex-col-reverse items-stretch justify-end gap-2 md:flex-row md:items-center">
					<RelationsPendingRequests relationRole="buyer" />

					<RelationsAddRequest
						relationRole="buyer"
						buttonLabel={t("relation.addBuyerButton")}
						dialogTitle={t("relation.addBuyerButton")}
					/>

					<AddPaymentDialog direction="receive" />
				</div>
			</div>

			<RelationsFilterForm onSearch={handleSearch} isSeller={false} />

			<DataTable
				columns={columns}
				data={relations?.results ?? []}
				totalItems={relations?.count ?? 0}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => row.id}
			/>

			{adjustmentRelation && (
				<BalanceAdjustmentDialog
					relation={adjustmentRelation}
					open={!!adjustmentRelation}
					onClose={() => setAdjustmentRelation(null)}
				/>
			)}
		</div>
	);
};

export default BuyerRelations;
